import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import { useNavigate, useSearchParams } from "react-router-dom";
import MasterCategoryTabs from "./components/MasterCategoryTabs";
import MasterEntryDialog from "./components/MasterEntryDialog";
import MasterEntryTable from "./components/MasterEntryTable";
import { masterCategories } from "./mockMasters";
import { mastersApi } from "../../../services/MasterDataServices";
import TablePagerBar from "../TicketManagement/shared/TablePagerBar";
import ConfirmDeleteDialog from "../TicketManagement/shared/ConfirmDeleteDialog";
import PageWrapper from "../../../common/PageWrapper/PageWrapper";

export default function MasterDataMain() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = searchParams.get("tab") || masterCategories[0]?.id;
  const page = Number(searchParams.get("page")) || 1;
  const perPage = Number(searchParams.get("perPage")) || 10;
  const pagination = { page, perPage };
  const updateParams = (u) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    Object.entries(u).forEach(([k, v]) => next.set(k, String(v)));
    return next;
  }, { replace: true });
  const setCategoryId = (id) => updateParams({ tab: id, page: 1 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState({ total: 0, next_page: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const displayEntries = useMemo(() => {
    return (entries || []).map((e) => ({
      ...e,
      status: e?.is_deleted ? "inactive" : "active",
    }));
  }, [entries]);

  const fetchEntries = (resource, page = pagination.page, perPage = pagination.perPage) => {
    if (!resource) {
      setEntries([]);
      setMeta({ total: 0, next_page: null });
      return;
    }
    mastersApi(resource, "get", null, { page, per_page: perPage })
      .then((res) => {
        setEntries(res?.data || []);
        const apiTotal = res?.meta?.total ?? res?.meta?.count;
        const nextPage = res?.meta?.next_page ?? null;
        const pageTotal = (Number(page) - 1) * Number(perPage) + (res?.data?.length ?? 0);
        setMeta({
          total: apiTotal ?? (nextPage === null ? pageTotal : (res?.data?.length ?? 0)),
          next_page: nextPage,
        });
      })
      .catch(() => {
        setEntries([]);
        setMeta({ total: 0, next_page: null });
      });
  };

  useEffect(() => {
    fetchEntries(categoryId, pagination.page, pagination.perPage);
  }, [categoryId, pagination.page, pagination.perPage]);


  const handleCreate = () => {
    setEditEntry(null);
    setOpenDialog(true);
  };

  const handleEdit = (entry) => {
    setEditEntry(entry);
    setOpenDialog(true);
  };

  const handleRowClick = (entry) => {
    if (!categoryId || !entry?.id) return;
    navigate(`/management/master-data/${categoryId}/${entry.id}`);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setEditEntry(null);
    fetchEntries(categoryId, pagination.page, pagination.perPage);
  };

  const handleSubmit = (payload) => {
    if (!categoryId) return;

    const id = payload?.id;
    const submitPayload = {
      ...payload,
    };
    delete submitPayload.id;
    // Removed ticket_fees specific status handling since ticket_fees is no longer in master data
    delete submitPayload.status;

    if (categoryId === "categories" || categoryId === "master_items") {
      delete submitPayload.code;
    }

    const req = id
      ? mastersApi(categoryId, "patch", submitPayload, null, id)
      : mastersApi(categoryId, "post", submitPayload);
    req
      .then(() => {
        fetchEntries(categoryId, pagination.page, pagination.perPage);
        handleClose();
      })
      .catch(() => {});
  };

  const requestDelete = (entryId) => {
    if (!entryId) return;
    setDeleteTarget({ id: entryId });
  };

  const handleChangePage = (_, page) => {
    // Prevent advancing beyond last page when API reports no next_page
    if (page > pagination.page && (meta?.next_page === null || meta?.next_page === undefined)) {
      return;
    }
    updateParams({ page });
  };

  const cancelDelete = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!categoryId || !deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      await mastersApi(categoryId, "delete", null, null, deleteTarget.id);
      setDeleteTarget(null);
      fetchEntries(categoryId, pagination.page, pagination.perPage);
    } catch {
    } finally {
      setDeleteLoading(false);
    }
  };

  // Server-side pagination: entries already represent current page
  const startIndex = useMemo(() => {
    const page = pagination.page || 1;
    const perPage = pagination.perPage || 10;
    return (page - 1) * perPage;
  }, [pagination.page, pagination.perPage]);

  return (
    <PageWrapper>
      <Box sx={{ mt: 2 }}>
        <MasterCategoryTabs categoryId={categoryId} onChange={setCategoryId} />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
        <Button variant={"contained"} onClick={handleCreate} className={"text-capitalize"}>
          Add Entry
        </Button>
      </Box>

      <Box sx={{ mt: 2 }}>
        <MasterEntryTable
          categoryId={categoryId}
          entries={displayEntries}
          startIndex={startIndex}
          onEdit={handleEdit}
          onLogicalDelete={requestDelete}
          onRowClick={handleRowClick}
        />

        <TablePagerBar
          perPage={pagination.perPage}
          onPerPageChange={(e) => updateParams({ perPage: Number(e.target.value), page: 1 })}
          total={meta?.total || 0}
          page={pagination.page}
          onPageChange={handleChangePage}
        />
      </Box>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title={"Delete"}
        description={"Are you sure you want to delete this entry?"}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />

      {openDialog && (
        <MasterEntryDialog
          open={openDialog}
          entry={editEntry}
          categoryId={categoryId}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      )}
    </PageWrapper>
  );
}
